import React, { useEffect, useState, useRef } from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import SparkApi from "../../api/SparkApi";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import BarcodeScannerComponent from "react-qr-barcode-scanner";

const InventoryCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [factoryOptions, setFactoryOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [rackOptions, setRackOptions] = useState([]);
  const [filteredRacks, setFilteredRacks] = useState([]);
  const [productQuantity, setProductQuantity] = useState(0);
  const generateUniqueId = () => Date.now().toString();
  const [searchText, setSearchText] = useState();
  const [isScannerVisible, setScannerVisible] = useState(false); // Control the scanner visibility

  const [inventoryItems, setInventoryItems] = useState([
    {
      uniqueId: generateUniqueId(),
      factory_id: location?.state?.factory_id || "",
      warehouse_id: location?.state?.warehouse_id || "",
      rack_id: location?.state?.rack_id || "",
      quantity: location?.state?.quantity || "",
      remark: location?.state?.remark || "",
      supplier: location?.state?.supplier || "",
      type: location?.state?.type || "check-in",
    },
  ]);

  const [productInventory, setProductInventory] = useState({
    product_id: "",
  });

  /*------  START Barcode ------*/
  const [barcodeRead, setBarcodeRead] = useState();
  const barcodeRef = useRef({
    timing: 0,
    data: "",
  });

  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = today.getFullYear();
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (productInventory?.product_barcode){
      fetchProductDetails();
    } 
  }, [productInventory?.product_barcode]);

  useEffect(() => {
    const barcodeReaded = () => {
      if (
        barcodeRef.current.data.length > 1 &&
        barcodeRef.current.data.includes("PRO")
      ) {
        setBarcodeRead(barcodeRef.current.data.split("E")[0]);
      }
      barcodeRef.current = { timing: 0, data: "" };
    };

    let timeout;

    const handleKeypress = (e) => {
      const currentTime = e.timeStamp;
      const isWithinInterval = currentTime - barcodeRef.current.timing < 200;

      if (barcodeRef.current.data.length === 0 || isWithinInterval) {
        clearTimeout(timeout);
        barcodeRef.current.data += e.key;
        barcodeRef.current.timing = currentTime;
        timeout = setTimeout(barcodeReaded, 300);
      } else {
        clearTimeout(timeout);
        barcodeRef.current = {
          timing: currentTime,
          data: e.key,
        };
      }
    };

    window.addEventListener("keypress", handleKeypress);

    return () => {
      window.removeEventListener("keypress", handleKeypress);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (barcodeRead) {
      fetchProductDetails();
    }
  }, [barcodeRead]);

  const fetchProductDetails = async () => {
    try {
      const updatedData = await SparkApi.getcheckoutData(barcodeRead || productInventory?.product_barcode);
      const filteredCheckoutRecord =  updatedData?.records?.filter((object) => object.quantity !== 0);

      setProductOptions([
        {
          value: updatedData?.records[0]?.product_id,
          label: updatedData?.records[0]?.product_name,
        },
      ]);
      
      if (updatedData?.records?.length === 0) {
        setProductInventory((prev) => ({
          ...prev,
          product_id: updatedData?.records[0]?.product_id,
          product_name: updatedData?.records[0]?.product_name,
          invent: [],
        }));
        return toast.error("No inventory found");
      }
      
      if(filteredCheckoutRecord.length === 0){
        setProductInventory((prev) => ({
          ...prev,
          product_id: updatedData?.records[0]?.product_id,
          product_name: updatedData?.records[0]?.product_name,
          invent: [],
        }));
        return toast.error("No inventory available for the selected product");
      }

      setProductInventory((prev) => ({
        ...prev,
        product_id: updatedData?.records[0]?.product_id,
        product_name: updatedData?.records[0]?.product_name,
        invent: filteredCheckoutRecord?.map((object, index) => ({
          ...object,
          type: "check-out",   // override — DB stores "check-in", backend skips rows unless this is "check-out"
          index: index,
          checkout_datetime: getCurrentDate(),
          isScan: false,
        })),
      }));
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  };
  /*---------   End barcode   ---------*/

  useEffect(() => {
    fetchFactories();
    fetchWarehouses();
    fetchRacks();
    if (location.state) {
      if(location.state?.productBarcode){
        setProductInventory((prev) => ({
          ...prev,
          product_barcode : location?.state?.productBarcode,
        }));
      }
    }
  }, [location.state]);

  const fetchFactories = async () => {
    try {
      const result = await SparkApi.fetchFactory();
      if (result && Array.isArray(result)) {
        setFactoryOptions(
          result.map((sub) => ({ value: sub.id, label: sub.name }))
        );
      } else if (result) {
        setFactoryOptions([{ value: result.id, label: result.name }]);
      }
    } catch (err) {
      console.log("Error", err);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const warehouses = await SparkApi.fetchWarehouse();
      setWarehouseOptions(
        warehouses.map((sub) => ({
          value: sub.id,
          label: sub.name,
          factory_id: sub.factory_id,
        }))
      );
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchRacks = async () => {
    try {
      const racks = await SparkApi.fetchRacks();
      setRackOptions(
        racks.data.map((rackRec) => ({
          value: rackRec.id,
          label: rackRec.name,
          warehouse_id: rackRec.warehouse_id,
        }))
      );
    } catch (error) {
      console.error("Error fetching racks:", error);
    }
  };

  const handleFactorySelect = (selectedOption, uniqueId) => {
    setInventoryItems((prevItems) =>
      prevItems?.map((item) =>
        item?.uniqueId === uniqueId
          ? { ...item, factory_id: selectedOption?.value }
          : item
      )
    );
  };

  const handleWarehouseSelect = (selectedOption, uniqueId) => {
    const filtered = rackOptions?.filter(
      (rack) => rack?.warehouse_id === selectedOption?.value
    );
    setFilteredRacks(filtered);

    setInventoryItems((prevItems) =>
      prevItems?.map((item) =>
        item?.uniqueId === uniqueId
          ? { ...item, warehouse_id: selectedOption?.value }
          : item
      )
    );

    getProductRelatedRacks(
      productInventory.product_id,
      selectedOption,
      uniqueId
    );
  };

  const handleRackSelect = (selectedOption, uniqueId) => {
    setInventoryItems((prevItems) =>
      prevItems?.map((item) =>
        item?.uniqueId === uniqueId
          ? { ...item, rack_id: selectedOption?.value }
          : item
      )
    );
  };

  const handleChange = (e, uniqueId) => {
    const { name, value } = e.target;
    const quantityValue = parseInt(value);
    if (isNaN(quantityValue)) {
      console.error("Invalid quantity value");
      return;
    }

    if (name === "quantity" && productInventory.product_id) {
      const isCheckOut = inventoryItems.some(
        (item) => item.type === "check-out" && item.uniqueId === uniqueId
      );
      const isCheckIn = inventoryItems.some(
        (item) => item.type === "check-in" && item.uniqueId === uniqueId
      );

      if (isCheckOut) {
        if (quantityValue > productQuantity) {
          alert("Cannot check out. Not enough stock.");
          return;
        } else {
          const updatedProductQuantity = productQuantity - quantityValue;
          setProductQuantity(updatedProductQuantity);
          setInventoryItems((prevItems) =>
            prevItems.map((item) =>
              item.uniqueId === uniqueId ? { ...item, [name]: value } : item
            )
          );
          return;
        }
      }

      if (isCheckIn) {
        const updatedProductQuantity = parseInt(productQuantity) + parseInt(quantityValue);

        setProductQuantity(updatedProductQuantity);
        setInventoryItems((prevItems) =>
          prevItems.map((item) =>
            item.uniqueId === uniqueId ? { ...item, [name]: value } : item
          )
        );
        return;
      }
    }

    setInventoryItems((prevItems) =>
      prevItems.map((item) =>
        item.uniqueId === uniqueId ? { ...item, [name]: value } : item
      )
    );
  };

  const handleProductChange = async (selected) => {

    setProductInventory((prev) => ({
      ...prev,
      product_id: selected ? selected.value : "",
      product_name: selected ? selected.label : "",
    }));

    if (selected?.value) {
      const updatedData = await SparkApi.getUpdatedFetchdata(selected.value);
      const filteredCheckoutRecord =  updatedData?.records?.filter((object) => object.quantity !== 0);
      
      if (updatedData?.records?.length === 0) {
        setProductInventory((prev) => ({
          ...prev,
          invent: [],
        }));
        alert('handle product change');
        return toast.error("No inventory found");
      }

      if(filteredCheckoutRecord.length === 0){
        setProductInventory((prev) => ({
          ...prev,
          product_id: updatedData?.records[0]?.product_id,
          product_name: updatedData?.records[0]?.product_name,
          invent: [],
        }))
        return toast.error("No inventory available for the selected product");
      }

      setProductInventory((prev) => ({
        ...prev,
        invent: filteredCheckoutRecord?.map((object, index) => ({
          ...object,
          type: "check-out",   // override — DB stores "check-in", backend skips rows unless this is "check-out"
          index: index,
          checkout_datetime: getCurrentDate(),
          isScan: false,
        })),
      }));
    }

    if (selected) {
      const product = productOptions?.find((product) => product.value === selected.value);
      if (product) {
        setProductQuantity(parseInt(product.quantity ? product.quantity : 0));
        getProductRelatedWHRelatedFactory(product);
      }
    }
  };

  const getProductRelatedWH = async (product) => {
    try {
      const resultWh = await SparkApi.getRelatedWarehouses(product?.value);
      if (resultWh.success) {
        setWarehouseOptions(
          resultWh?.response?.map((sub) => ({
            value: sub.warehouse_id,
            label: sub.warehouse_name,
            factory_id: sub.factory_id,
          }))
        );
      }
    } catch (error) {
      console.log("error->", error);
    }
  };

  const getProductRelatedWHRelatedFactory = async (product) => {
    try {
      const resultFactory = await SparkApi.getWarehousesTRelatedFactory(product?.value);
      if (resultFactory.success) {
        setFactoryOptions(
          resultFactory?.response?.map((sub) => ({
            value: sub.factory_id,
            label: sub.name,
          }))
        );
        getProductRelatedWH(product);
      }
    } catch (error) {
      console.log("error->", error);
    }
  };

  const getProductRelatedRacks = async (product_id, whRecord, uniqueId) => {
    try {
      const resultRacks = await SparkApi.getRelatedRacks(
        product_id,
        whRecord?.value
      );
      if (resultRacks.success) {
        setFilteredRacks(
          resultRacks?.response?.map((sub) => ({
            value: sub.rack_id,
            label: sub.rack_name,
          }))
        );
        setInventoryItems((prevItems) =>
          prevItems.map((item) =>
            item.uniqueId === uniqueId
              ? { ...item, rack_id: whRecord.value }
              : item
          )
        );
      }
    } catch (error) {
      console.log("error -->", error);
    }
  };

  const handleCancel = () => {
    // navigate("/productinventory");
    navigate("/products");
  };

  const fetchProductsBySearchedText = async () => {
    try {
      if (searchText.trim()) {
        const products = await SparkApi.getProductsBySearch(searchText);
        if (products.success) {
          setProductOptions(
            products.records.map((ProRec) => ({
              value: ProRec.id,
              label: ProRec.name,
              quantity: ProRec.quantity,
            }))
          );
        }
      } else {
        setProductOptions([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleCheckOut = (e, uniqueId, checkinQty, rowIndex) => {
    const { name, value } = e.target;
    const availableQty = parseInt(checkinQty) || 0;

    // Allow empty string while typing — just clear the stored value
    if (value === '') {
      setProductInventory((prev) => ({
        ...prev,
        invent: prev.invent.map((item) =>
          item.index === rowIndex ? { ...item, [name]: '', isScan: false } : item
        ),
      }));
      return;
    }

    const enteredQty = parseInt(value);
    if (isNaN(enteredQty) || enteredQty < 0) return; // ignore non-numeric

    if (enteredQty > availableQty) {
      return toast.error(`Cannot check out more than available stock (${availableQty}).`);
    }

    setProductInventory((prev) => ({
      ...prev,
      invent: prev.invent.map((item) =>
        item.index === rowIndex
          ? { ...item, [name]: enteredQty, isScan: enteredQty > 0 }
          : item
      ),
    }));
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      if (!productInventory.product_id) {
        return toast.error("Please select a product");
      }

      if (productInventory?.invent?.length === 0) {
        return toast.error("Please add at least one inventory item");
      }
      
      const result = await SparkApi.createProductInventory(productInventory);

      if (result.success) {
        navigate("/productinventory");
      } else {
        console.error("API call failed:", result?.message || "Unknown error");
      }
    } catch (error) {
      console.error("Error submitting product:", error);
    }
  };

  const handleScanButton = () => {
    setScannerVisible(true);
  };

  return (
    <Container>
      <ToastContainer
        theme="light"
        autoClose={2000}
        position="top-center"
        pauseOnHover
      />

      <div style={{ textAlign: "center" }}>
        {isScannerVisible && (
          <>
            <BarcodeScannerComponent
              width={250}
              height={250}
              facingMode="environment"
              onUpdate={(err, result) => {
                if (result){
                  setBarcodeRead(result?.text);
                  setScannerVisible(false);
                } 
                else {
                  setBarcodeRead(null)
                  // setScannerVisible(false);
                }
              }}
            />
            {/* <button onClick={handleStopScan}>Stop Scanning</button> */}
          </>
        )}
        {/* <p>Scanned Data: {barcodeRead}</p> */}
      </div>

      <Row className="mt-3">
        <Col lg={10}>
          <Link className="nav-link" to="/products">
            Home <i className="fa-solid fa-chevron-right"></i> {location?.state?.productBarcode ? 'Product ' : `Products `}<i className="fa-solid fa-chevron-right"></i>
            <span style={{ color: "#23468c" }}> Check Out</span>
          </Link>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col lg={4} xs={12}>
          <Select
            id="product_id"
            name="product_id"
            options={productOptions}
            value={productOptions?.find(
              (option) => option?.value === productInventory.product_id
            )}
            onInputChange={(inputValue) => {
              setSearchText(inputValue);
              fetchProductsBySearchedText();
            }}
            onChange={(select) => handleProductChange(select)}
            isClearable={true}
            isSearchable={true}
            placeholder="--Enter Product Name--"
            required
          />
        </Col>
        <Col lg={4}></Col>
        <Col lg={4} className="text-start text-sm-end checkin-checkout-drop-dwon-button-margion-top">
          <Button onClick={handleCancel} variant="danger" className="mx-2 btn-sm">Cancel</Button>
          <Button variant="success" className="btn-sm" onClick={handleSubmit} disabled={!productInventory.product_id}>
            Save
          </Button>
          <Button className="btn-sm scanButton" onClick={handleScanButton}>Scan Barcode</Button>
        </Col>
      </Row>

      <Row className="mt-2">
        <Table bordered responsive className="checkout-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Factory</th>
              <th>Warehouse</th>
              <th>Rack</th>
              <th>Quantity</th>
              <th>Check out</th>
              <th>Type</th>
              <th>Remark</th>
              <th>Supplier</th>
            </tr>
          </thead>
          <tbody>
            {productInventory?.invent?.map((item) => (
              <tr key={item.id || item.index}>
                <td>
                  <Form.Control
                    disabled={!item?.isScan && location?.state}
                    type="date"
                    value={item?.checkout_datetime}
                    onChange={(e) => {
                      const newDate = e.target.value;
                      setProductInventory((prevInventory) => ({
                        ...prevInventory,
                        invent: prevInventory.invent.map((obj, index) =>
                          item?.index === index
                            ? {
                                ...obj,
                                checkout_datetime: newDate,
                                isScan: true,
                              }
                            : obj
                        ),
                      }));
                    }}
                  />
                </td>

                <td>
                  <Select
                    isDisabled
                    name="factory_id"
                    options={factoryOptions || []}
                    value={factoryOptions?.find(
                      (option) => option?.value === item?.factory_id
                    )}
                    onChange={(selected) =>
                      handleFactorySelect(selected, item.uniqueId)
                    }
                    isClearable={true}
                    required
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    menuPosition="fixed"
                  />
                </td>

                <td>
                  <Select
                    isDisabled
                    name="warehouse_id"
                    options={warehouseOptions}
                    value={warehouseOptions.find(
                      (option) => option.value === item.warehouse_id
                    )}
                    onChange={(selected) =>
                      handleWarehouseSelect(selected, item.uniqueId)
                    }
                    isClearable
                    placeholder="-- Select --"
                    required
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    menuPosition="fixed"
                  />
                </td>
                <td>
                  <Select
                    isDisabled
                    name="rack_id"
                    // options={filteredRacks}
                    options={rackOptions}
                    value={rackOptions.find(
                      (option) => option.value === item.rack_id
                    )}
                    onChange={(selected) =>
                      handleRackSelect(selected, item.uniqueId)
                    }
                    isClearable
                    placeholder="-- Select Rack --"
                    required
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                    menuPosition="fixed"
                  />
                </td>
                <td>
                  <Form.Control
                    disabled
                    type="numeric"
                    placeholder="--Enter quantity--"
                    name="quantity"
                    value={item.quantity}
                    onChange={(e) => handleChange(e, item.uniqueId)}
                    required
                  />
                </td>
                <td>
                  <Form.Control
                    type="number"
                    min="0"
                    max={parseInt(item.quantity) || 0}
                    placeholder="--Check Out Qty--"
                    name="checkout_quantity"
                    value={item?.checkout_quantity ?? ''}
                    onChange={(e) =>
                      handleCheckOut(e, item.uniqueId, item.quantity, item.index)
                    }
                    style={{ minWidth: "100px" }}
                  />
                </td>

                <td>
                  <Form.Select
                    required
                    disabled
                    name="type"
                    value={(item.type = "check-out" || "")}
                    onChange={(e) => {
                      setInventoryItems((prevItems) => {
                        const updatedItems = prevItems.map((prevItem) => {
                          if (prevItem.uniqueId === item.uniqueId) {
                            return { ...prevItem, type: "check-out" };
                          }
                          return prevItem;
                        });
                        return updatedItems;
                      });
                    }}
                  >
                    <option value="" disabled>
                      -- Select Type --
                    </option>
                    <option value="check-in">Check-in</option>
                    <option value="check-out">Check-out</option>
                  </Form.Select>
                </td>

                <td>
                  <Form.Control
                    disabled={!item?.isScan}
                    type="text"
                    placeholder="-- Remark --"
                    name="remark"
                    value={item?.remark}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setProductInventory((prev) => ({
                        ...prev,
                        invent: prev?.invent?.map((obj, index) =>
                          index === item?.index
                            ? { ...obj, [name]: value }
                            : obj
                        ),
                      }));
                    }}
                  />
                </td>
                <td>
                  <Form.Control
                    disabled={!item?.isScan}
                    type="text"
                    placeholder="-- Supplier --"
                    name="supplier"
                    value={item?.supplier}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setInventoryItems((prevItems) =>
                        prevItems?.map((prevItem) =>
                          prevItem.uniqueId === item?.uniqueId
                            ? { ...prevItem, [name]: value }
                            : prevItem
                        )
                      );
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Row>
    </Container>
  );
};
export default InventoryCheckout;